import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { useIntl } from "../Intl/Context";
import { useMemo } from "react";

export const useLocalizedLocationChangeEffect = (callback, dependencies = []) => {
  const location = useLocalizedLocation();
  const { pathname } = location;
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    // Do not process the first rendering as url redirections are handled server side on
    // the first page load.
    if (pathnameRef.current === pathname) return;

    pathnameRef.current = pathname;

    callback(location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, location, pathname, ...dependencies]);
};

export const useLocalizedLocation = () => {
  const intl = useIntl();
  const routerLocation = useLocation();

  const location = useMemo(() => {
    // Ensure that the location's pathname has the right locale prefix if applicable
    const pathname = localizePathname(routerLocation.pathname, intl);

    return { ...routerLocation, pathname };
  }, [intl, routerLocation]);

  return location;
};

export const localizePathname = (pathname, intl) => {
  const { defaultLocale, locale } = intl;
  // The regex uses (/|$) to make the trailing slash optional. This ensures we correctly
  // detect paths like "/ca", "/ca/", and "/ca/page" to prevent duplicate locale prefixes
  // (e.g., "/ca/ca" when clicking the logo from a localized page).
  const localePrefixRegex = new RegExp(`^/${locale}(/|$)`);

  // If the locale is not the default locale, and the route doesn't already include the
  // locale at the beginning of its path, we add it here.
  if (locale === defaultLocale || pathname.match(localePrefixRegex)) return pathname;

  return `/${locale}${pathname}`;
};

export const unlocalizePathname = pathname => {
  return pathname.replace(/^\/\w{2}(\/|$)/, "/");
};
